import { Injectable } from '@angular/core';
import { Observable, lastValueFrom } from 'rxjs';
import { HttpHeaders, HttpParams, HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { APIS } from '../enums/apis';
import { Provider } from 'src/app/shared/models/provider';
import { RestUtilitiesService } from './rest-utilities.service';
import { HttpBaseResponse } from 'src/app/shared/models/httpBaseResponse';
import { Specialty } from 'src/app/shared/models/specialty';
import { Address } from 'src/app/shared/models/address';
import { Nationality } from 'src/app/shared/models/nationality';
import { Location } from 'src/app/shared/models/location';
import { PROVIDER_STATUS } from '../enums/providerStatus';

export interface ProviderLocation {
   id?: number;
   location: Location;
   latitude?: number;
   longitude?: number
 }

export interface ProvidersQPS {
   active?: boolean;
   caseId?: number,
   expand?: string[],
   fullName?: string;
   fullName_like?: string;
   isCorporate?: boolean,
   isFilteredByRegion?: boolean
   isSupply?: boolean;
   page?: number,
   size?: number,
   specialtyId?: number;
   id?: number;
}

export interface ProvidersBySpecialtyQPS {
    active: boolean;
    status?:PROVIDER_STATUS;
    specialtyId:number;
}

export interface NationsQPS {
   name_like?: string;
}

export interface CuitQPS {
    cuit?: number;
 }

export interface PostProviderBody {
   name?: string;
   cuit?: number;
   surname?: string;
   mobilePhoneNumber?: string;
   address?: Address;
   photo?: string;
   specialties?: Specialty[];
   status?: PROVIDER_STATUS;
   observations?:string;
}

export interface PatchProviderBody extends PostProviderBody {
   active?: boolean;
}


@Injectable({
   providedIn: 'root',
})
export class ProvidersService {
   constructor(private http: HttpClient, private restUtilitiesService: RestUtilitiesService) { }

   getProviders(qps?: ProvidersQPS): Observable<HttpBaseResponse<Provider[]>> {
      if (!qps) { qps = { active: true } }
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      const qpsProcessed: { param: string, value: string }[] = this.restUtilitiesService.formatQPs(qps);
      const queryParams: HttpParams = this.restUtilitiesService.createAndAppendQps(qpsProcessed);
      return this.http
         .get<any>(`${environment.apiProviderServer}${APIS.PROVIDERS}`, {
            headers: queryHeaders,
            observe: 'response',
            params: queryParams,
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               response.body.data = this.adaptProviders(response.body.data);
               return response.body;
            }),
         );
   }

   async getProvidersBySpecialty( qps?: ProvidersBySpecialtyQPS ): Promise<HttpBaseResponse<Provider[]>> {
    const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
    const qpsProcessed = this.restUtilitiesService.formatQPs(qps);
    const queryParams: HttpParams = this.restUtilitiesService.createAndAppendQps(qpsProcessed);
    const obs$ = this.http
    .get<any>(`${environment.apiProviderServer}${APIS.PROVIDERS_BY_SPECIALTY}/`, {
        headers: queryHeaders,
        observe: 'response',
        params: queryParams,
     })
      .pipe(
        map((response: HttpResponse<any>) => {
          response.body.data = this.adaptProviders(response.body.data);
          return response.body as HttpBaseResponse<Provider[]>;
        })
      );
    return await lastValueFrom(obs$);
  }

   private adaptProviders(providers: Provider[]) {
      return providers.map(
         provider => {
            provider.name = provider.name.replace(",", "");
            return provider;
         });
   }

   postProvider(entidad: PostProviderBody): Observable<Provider> {
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      return this.http
         .post<any>(`${environment.apiProviderServer}${APIS.PROVIDERS}`, JSON.stringify(entidad), {
            headers: queryHeaders,
            observe: 'response',
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               return response.body.data;
            }),
         );
   }

   patchProvider(entidad: PatchProviderBody, entidadId) {
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      return this.http
         .patch<any>(
            `${environment.apiProviderServer}${APIS.PROVIDERS}/${entidadId}`,
            JSON.stringify(entidad),
            {
               headers: queryHeaders,
               observe: 'response',
            },
         )
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               return response.body.data;
            }),
         );
   }

   getProvidersCount(): Observable<any> {
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      return this.http
         .get<any>(`${environment.apiProviderServer}${APIS.PROVIDERS}/count`, {
            headers: queryHeaders,
            observe: 'response',
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               return response.body.data;
            }),
         );
   }

   getProviderMedia(id:number,mediaUUID:string): Observable<any> {
    const qps = {
        id,
        mediaUUID
    }
    const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
    const qpsProcessed: { param: string, value: string }[] = this.restUtilitiesService.formatQPs(qps);
    const queryParams: HttpParams = this.restUtilitiesService.createAndAppendQps(qpsProcessed);
    return this.http
       .get<any>(`${environment.apiProviderServer}${APIS.MEDIA}/downloadfile`, {
          headers: queryHeaders,
          observe: 'response',
          params: queryParams,
       })
       .pipe<any>(
          map<HttpResponse<any>, any>((response) => {
             return response.body.data;
          }),
       );
   }


   public getSignaturePhoto(providerId: number): Observable<any> {
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      return this.http
         .get<any>(`${environment.apiProviderServer}${APIS.PROVIDERS}/${providerId}/signature`, {
            headers: queryHeaders,
            observe: 'response',
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               return response.body.data;
            }),
         );
   }

   getMediaByProviderId(providerId: number): Observable<any> {
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      return this.http
         .get<any>(`${environment.apiProviderServer}${APIS.PROVIDERS}/${providerId}/media`, {
            headers: queryHeaders,
            observe: 'response',
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               return response.body.data;
            }),
         );
   }

   public getCuit(qps: CuitQPS): Observable<boolean> {
    const params = this.restUtilitiesService.createAndAppendQps(
      this.restUtilitiesService.formatQPs(qps)
    );

    return this.http
      .get<HttpBaseResponse<{ isCuitRepeated: boolean }>>(
        `${environment.apiProviderServer}${APIS.PROVIDERS}/cuit`,
        { params }
      )
      .pipe(
        map(res => !!res?.data?.isCuitRepeated)
      );
  }


   getNations(qps: NationsQPS): Observable<Nationality[]> {
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      const qpsProcessed: { param: string, value: string }[] = this.restUtilitiesService.formatQPs(qps);
      const queryParams: HttpParams = this.restUtilitiesService.createAndAppendQps(qpsProcessed);
      return this.http
         .get<any>(`${environment.apiProviderServer}/user/nations`, {
            headers: queryHeaders,
            observe: 'response',
            params: queryParams,
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               return response.body.data;
            }),
         );
   }

    getProvidersLight(qps?: ProvidersQPS): Observable<HttpBaseResponse<Provider[]>> {
    if (!qps) { qps = { active: true } }
      const queryHeaders = new HttpHeaders().append('Content-Type', 'application/json');
      const qpsProcessed: { param: string, value: string }[] = this.restUtilitiesService.formatQPs(qps);
      const queryParams: HttpParams = this.restUtilitiesService.createAndAppendQps(qpsProcessed);
      return this.http
         .get<any>(`${environment.apiProviderServer}${APIS.PROVIDERS}/light`, {
            headers: queryHeaders,
            observe: 'response',
            params: queryParams,
         })
         .pipe<any>(
            map<HttpResponse<any>, any>((response) => {
               response.body.data = this.adaptProviders(response.body.data);
               return response.body;
            }),
         );
   }
}
